import { CardBlock } from '@sbt-web/ancillary-services';
import { AdTypes, CategoryID, GeoEntry } from '@sbt-web/network/types';
import { useViewport } from '@sbt-web/hooks';
import { HebeCampaignPosition } from '@sbt-web/networking';
import { prepareRemoteDataListing } from '@shared/helpers/Hebe';
import { GeoboostItem, Item } from '@shared/models';
import React, { useContext } from 'react';
import { ExpandSearch } from '../Elements/ExpandSearch';
import GalleryCTA from '../Elements/GalleryCTA';
import { AdItem } from './AdItem';
import { getOrCreatePulse } from '@tools/tracking/utils';
import { addSmartADVItem } from './SmartAdvItem';
import { isThisPlacementEnabled } from './SmartAdvItem/helpers';
import {
  OptimizelySubitoContext,
  OptimizelySubitoFeature,
} from '@sbt-web/houston-wrapper';
import { getErrorMessage } from '@tools/errorHelpers';
import Geoboost from '@client/components/Adv/Geoboost';
import { GEOBOOST_TOGGLE } from '@client/components/Adv/Geoboost/shared';
import type { FiltersStore } from '@tools/search/getFiltersStore';
import { VerifiedUserFakeDoorLoader } from '@client/components/Adv/VerifiedUserFakeDoor/loader';

export interface ListingItemProps {
  item: Item;
  index: number;
  listingCategory: CategoryID;
  adType: AdTypes;
  filters: Record<string, string>;
  filtersLabels?: Record<string, string>;
  query: string;
  geo: GeoEntry;
  macroCategory: string;
  handleItemClick: () => void;
  shouldHideProDetails?: boolean;
  enableOverrideUrgentForPro: boolean;
  filterStore: FiltersStore | null;
}

export const ListingItem: React.FunctionComponent<ListingItemProps> = (
  listingProps
) => {
  const { isMobile } = useViewport();
  const { optimizely } = useContext(OptimizelySubitoContext);
  const props = { ...listingProps, isMobile };
  const { item } = props;

  const handleItemClick = () => {
    if (item.kind === 'GalleryItem') {
      optimizely?.track('click_on_vetrina_ad');
    }
    props.handleItemClick?.();
  };

  // Define with ad kind if interstitial is enable
  function isInterstitialEnable() {
    return (
      item.kind === 'AdItem' ||
      item.kind === 'GeoboostItem' ||
      item.kind === 'DecoratedItem' ||
      item.kind === 'GalleryItem'
    );
  }

  try {
    return (
      <>
        {/* Add interstitial */}
        {isInterstitialEnable() ? (
          renderElement(props, isMobile, handleItemClick)
        ) : (
          <div data-google-interstitial="false">
            {renderElement(props, isMobile, handleItemClick)}
          </div>
        )}
      </>
    );
  } catch (error) {
    const { index, item } = props;
    console.error(
      `An error occurred while rendering the item at position ${index}`,
      getErrorMessage(error),
      JSON.stringify(item)
    );
    return null;
  }
};

function renderElement(
  props: ListingItemProps,
  isMobile: boolean,
  handleItemClick: () => void
) {
  const {
    item,
    index,
    listingCategory,
    adType,
    query,
    geo,
    filters,
    macroCategory,
    enableOverrideUrgentForPro,
    shouldHideProDetails,
  } = props;

  switch (item.kind) {
    case 'AdItem':
      return (
        <AdItem
          item={item}
          index={index}
          listingCategory={listingCategory}
          handleItemClick={handleItemClick}
          enableOverrideUrgentForPro={enableOverrideUrgentForPro}
          shouldHideProDetails={shouldHideProDetails}
        />
      );

    case 'SmartADVItem':
      if (!isThisPlacementEnabled(item, null)) {
        return;
      }

      return addSmartADVItem(item, listingCategory, isMobile);
    case 'CardBlockItem':
      return addCardBlockItem({ ...props, isMobile });
    case 'GalleryPlaceholder':
      return;
    case 'GalleryCTAItem':
      return addGalleryCTAItem(isMobile);
    case 'GalleryItem':
      return (
        <AdItem
          item={item.adItem}
          index={index}
          listingCategory={listingCategory}
          handleItemClick={handleItemClick}
          enableOverrideUrgentForPro={enableOverrideUrgentForPro}
          shouldHideProDetails={shouldHideProDetails}
        />
      );
    case 'GeoboostItem':
      return addGeoboostItem(item);
    case 'VerifiedUserFakeDoorItem':
      return addVerifiedUserFakeDoorItem();
    case 'DecoratedItem':
      return (
        <React.Fragment key={`${index}-decorated`}>
          {item.before.map((plugin, i) => (
            <div className="items__item" key={`before_${index}_${i}`}>
              <ListingItem
                item={plugin.model}
                index={index}
                listingCategory={listingCategory}
                adType={adType}
                filters={filters}
                query={query}
                geo={geo}
                macroCategory={macroCategory}
                handleItemClick={handleItemClick}
                enableOverrideUrgentForPro={enableOverrideUrgentForPro}
                filterStore={props.filterStore}
                shouldHideProDetails={shouldHideProDetails}
              />
            </div>
          ))}
          <AdItem
            item={item.item}
            index={index}
            listingCategory={listingCategory}
            handleItemClick={handleItemClick}
            enableOverrideUrgentForPro={enableOverrideUrgentForPro}
            shouldHideProDetails={shouldHideProDetails}
          />
          {item.after.map((plugin, i) => (
            <div
              id={`ad_wrapper_${index}_${i}`}
              className="items__item"
              key={`after_${index}_${i}`}
            >
              <ListingItem
                item={plugin.model}
                index={index}
                listingCategory={listingCategory}
                adType={adType}
                filters={filters}
                query={query}
                geo={geo}
                macroCategory={macroCategory}
                handleItemClick={handleItemClick}
                enableOverrideUrgentForPro={enableOverrideUrgentForPro}
                filterStore={props.filterStore}
                shouldHideProDetails={shouldHideProDetails}
              />
            </div>
          ))}
        </React.Fragment>
      );
    case 'ExpandSearchItem':
      return <ExpandSearch key="expand-search" withContainer />;
  }
}

ListingItem.displayName = 'ListingItem';

function addCardBlockItem(props: ListingItemProps & { isMobile: boolean }) {
  const { listingCategory, query, geo, filters, isMobile, filterStore } = props;
  const { adType } = props;

  return (
    <CardBlock
      flex
      reverse
      pulseInstance={getOrCreatePulse()}
      border
      remoteData={prepareRemoteDataListing(
        HebeCampaignPosition.NINTH_POSITION,
        adType,
        listingCategory,
        geo,
        filters,
        query,
        isMobile,
        filterStore
      )}
    />
  );
}

function addGalleryCTAItem(isMobile: boolean) {
  return !isMobile && <GalleryCTA key="gallery-cta" />;
}

function addGeoboostItem(item: GeoboostItem) {
  if (!item.boostedItems.length) {
    return null;
  }

  return (
    <OptimizelySubitoFeature feature={GEOBOOST_TOGGLE}>
      {(isEnabled) => {
        return isEnabled /* NOSONAR */ ? (
          <Geoboost items={item.boostedItems} />
        ) : null;
      }}
    </OptimizelySubitoFeature>
  );
}

function addVerifiedUserFakeDoorItem() {
  return <VerifiedUserFakeDoorLoader />;
}
